import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Flex,
  HStack,
  Heading,
  Skeleton,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  useGetFundingRequestQuery,
  useGetWorkflowInstanceIdForFundingRequestQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { SingleEntityDrawer as FundingRequestEventLogSingleEntityDrawer } from '../EntityEventLogDrawers';
import * as BorrowerDetailViewContainer from '~/borrower/domains/fundingRequests/ui/detail/ViewContainer';
import * as BorrowerProviders from '~/borrower/domains/fundingRequests/ui/manage/Context';
import * as BorrowerManageViewContainer from '~/borrower/domains/fundingRequests/ui/manage/ViewContainer';
import * as BorrowerViewTemplate from '~/borrower/domains/fundingRequests/ui/ViewTemplate';
import {
  ApproveFundingRequestButton,
  RejectFundingRequestButton,
} from '~/capitalProvider/domains/fundingRequests/ui/detail/ApproveRejectButtons';
import { FundingRequestInternalApproval } from '~/capitalProvider/domains/fundingRequests/ui/detail/FundingRequestInternalApproval';
import * as LenderViewContainer from '~/capitalProvider/domains/fundingRequests/ui/detail/ViewContainer';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionGuard } from '~/core/PermissionGuard';
import { permissions as createFundingRequestPermissions } from '~/core/routes/funding-requests/create/_index';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Timestamp } from '~/packages/common-frontend/components/Timestamp';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { truncateUuid } from '~/packages/common-frontend/utils';
import { FundingRequestTemplateProviderV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { CancelFundingRequestButton } from '~/packages/funding-requests/ui/shared/CancelFundingRequestButton';
import { FundingRequestStatusBadge } from '~/packages/funding-requests/ui/shared/StatusBadge';
import { ActionsButton } from '~/packages/funding-requests/ui/submit/ActionsButton';
import {
  SubmitFundingRequest,
  SubmitFundingRequestForAlphaCustomers,
} from '~/packages/funding-requests/ui/submit/index';
import { IdPill } from '~/packages/loans/ui/components/IdPill';

// Types
const RouteParamsStruct = z.object({
  fundingRequestId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
const LenderRouteBase = () => {
  const { state: locationState } = useLocation();
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);
  const flags = useFeatureFlags();
  const {
    user: { organizationId },
  } = useUserAuthOrThrow();

  const { data, error, loading } = useGetFundingRequestQuery({
    variables: { fundingRequestId },
  });
  if (error) throw error;

  const workflowInstanceForFundingRequestQuery = useGetWorkflowInstanceIdForFundingRequestQuery(
    {
      variables: {
        fundingRequestId,
        organizationId,
      },
    },
  );
  if (workflowInstanceForFundingRequestQuery.error) {
    Sentry.captureException(workflowInstanceForFundingRequestQuery.error);
    throw Error(
      `[Workflows] Workflow Instance for Funding Request query error. Contact Finley Support.`,
    );
  }
  const workflowInstanceId = useMemo(() => {
    if (workflowInstanceForFundingRequestQuery.data) {
      return workflowInstanceForFundingRequestQuery.data
        .getWorkflowInstanceIdForFundingRequest;
    }
    return null;
  }, [workflowInstanceForFundingRequestQuery.data]);

  if (!data || loading || !workflowInstanceForFundingRequestQuery.data) {
    return <Skeleton height="600px" />;
  }

  if (flags.displayFundingRequestV2ProviderV2) {
    return (
      <p>
        Hi I am a placeholder for the new provider route for
        /funding-requests/:id routes
      </p>
    );
  }

  if (flags.displayFundingRequestV2) {
    return (
      <FundingRequestTemplateProviderV1
        mode={
          data?.fundingRequestWithFullDetails.type === 'origination'
            ? 'create'
            : 'upsize'
        }
        allowedActions="readOnly"
        creditFacilityId={data.fundingRequestWithFullDetails.creditFacilityId}
        fundingRequestId={fundingRequestId}
        loanId={data.fundingRequestWithFullDetails.loans[0]?.id ?? ''} // TODO: multi-loan @sachinr4
      >
        <Flex direction="column" gap="1.5rem">
          {/* ------ 1. Breadcrumbs + status + actions row ------ */}
          <HStack justify="space-between" w="100%">
            <Breadcrumbs
              breadcrumbs={[
                { label: 'Funding Requests', path: '/funding-requests' },
                {
                  label:
                    data.fundingRequestWithFullDetails.creditFacility.name ||
                    'Funding Request',
                },
              ]}
            />
            <Flex alignItems="center" gap="1rem" justifyContent="end">
              <FundingRequestStatusBadge
                status={data.fundingRequestWithFullDetails.status}
              />
              <ActionsButton
                reports={
                  data.fundingRequestWithFullDetails.fundingRequestReports ?? []
                }
              />
              <FundingRequestEventLogSingleEntityDrawer
                entityId={fundingRequestId}
              />
            </Flex>
          </HStack>

          {/* ------ 2. Title and FR info row ------ */}
          <VStack w="100%" spacing="1rem" alignItems="start">
            <HStack
              flexWrap="wrap"
              justifyContent="space-between"
              spacing="1rem"
              w="100%"
            >
              <Typography variant="page-title" trim>
                {data.fundingRequestWithFullDetails.type === 'origination'
                  ? 'Originate Funding Request'
                  : 'Upsize Funding Request'}
              </Typography>

              <HStack gap="1rem">
                <IdPill label="ID" value={fundingRequestId} />
                {data.fundingRequestWithFullDetails.loans?.map((loan) => (
                  <IdPill
                    key={loan.id}
                    to={`/loans/${loan.id}`}
                    label="Loan ID"
                    value={
                      loan.externalLoanId
                        ? `${truncateUuid(loan.id)} (${loan.externalLoanId})`
                        : `${truncateUuid(loan.id)}`
                    }
                  />
                ))}
              </HStack>
            </HStack>
            {data.fundingRequestWithFullDetails.submittedAt && (
              <HStack>
                <span>
                  <Typography inline variant="detail-muted">
                    Submitted at{' '}
                  </Typography>
                  <Timestamp
                    timestamp={data.fundingRequestWithFullDetails.submittedAt}
                    variant="datetime"
                  />
                </span>
              </HStack>
            )}
          </VStack>

          {locationState?.from &&
            locationState.from === '/funding-requests/create' && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  A funding request is already pending approval. Wait for it to
                  be approved or cancel it to submit a new request.
                </AlertDescription>
              </Alert>
            )}
          {locationState?.from &&
            locationState.from === '/funding-requests/upsize' && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  A funding request is already pending approval. Wait for it to
                  be approved or cancel it to submit a new request.
                </AlertDescription>
              </Alert>
            )}
          {/* @jendevelops TODO: consolidate controls when lender + borrower on same routes again */}
          <SubmitFundingRequest
            status={data.fundingRequestWithFullDetails.status}
            fundingRequestId={fundingRequestId}
            type={data.fundingRequestWithFullDetails.type}
          />
          {/* If there is a workflow instance for this funding request and has "submitted" status, then show EDIT approval UI. */}
          {data.fundingRequestWithFullDetails.status === 'submitted' &&
            (workflowInstanceId ? (
              <FundingRequestInternalApproval
                fundingRequestId={fundingRequestId}
                workflowInstanceId={workflowInstanceId}
                creditFacilityId={
                  data.fundingRequestWithFullDetails.creditFacilityId
                }
                creditFacilityName={
                  data.fundingRequestWithFullDetails.creditFacility.name
                }
              />
            ) : (
              <Flex
                mt="25px"
                box-shadow="0px -2px 4px -1px rgba(0, 0, 0, 0.06)"
                w="100%"
                alignItems="center"
                justifyContent="flex-end"
                gap="16px"
              >
                <RejectFundingRequestButton
                  fundingRequest={data.fundingRequestWithFullDetails}
                />
                <ApproveFundingRequestButton
                  fundingRequest={data.fundingRequestWithFullDetails}
                />
              </Flex>
            ))}
          {/* If there is a workflow instance for this funding request and it has already been approved or rejected, show READ ONLY approval UI. */}
          {workflowInstanceId &&
            (data.fundingRequestWithFullDetails.status === 'rejected' ||
              data.fundingRequestWithFullDetails.status === 'approved') && (
              <FundingRequestInternalApproval
                fundingRequestId={fundingRequestId}
                workflowInstanceId={workflowInstanceId}
                creditFacilityId={
                  data.fundingRequestWithFullDetails.creditFacilityId
                }
                creditFacilityName={
                  data.fundingRequestWithFullDetails.creditFacility.name
                }
                readOnly
              />
            )}
        </Flex>
      </FundingRequestTemplateProviderV1>
    );
  }

  return (
    <LenderViewContainer.FundingRequestDetailViewContainer
      fundingRequestId={fundingRequestId}
      creditFacilityId={data.fundingRequestWithFullDetails.creditFacilityId}
    />
  );
};

const BorrowerRouteBaseOld = () => {
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);

  // Converts hashFrangment to desired management step
  const location = useLocation();
  const desiredStep = useMemo(() => {
    if (_.isEmpty(location.hash)) return null;
    const fragmentString = _.trimStart(location.hash, '#');
    return BorrowerManageViewContainer.isFundingRequestManageStep(
      fragmentString,
    )
      ? (fragmentString as BorrowerManageViewContainer.FundingRequestManageStep)
      : null;
  }, [location.hash]);

  const fundingRequestQuery = useGetFundingRequestQuery({
    variables: { fundingRequestId },
  });

  const [isCurrent, setIsCurrent] = useState(false);
  useEffect(() => {
    if (fundingRequestQuery.data) {
      setIsCurrent(
        !!fundingRequestQuery.data.fundingRequestWithFullDetails.isCurrent,
      );
    }
  }, [fundingRequestQuery.data]);
  if (fundingRequestQuery.error) throw fundingRequestQuery.error;
  if (!fundingRequestQuery.data || fundingRequestQuery.loading) {
    return <Skeleton height="600px" />;
  }

  if (isCurrent) {
    return (
      <BorrowerViewTemplate.FundingRequestsViewTemplate
        fundingRequestId={fundingRequestId}
        isCreditFacilitySelectDisabled
      >
        <BorrowerProviders.FundingRequestsProvider
          borrowerKey={
            fundingRequestQuery.data.fundingRequestWithFullDetails
              .creditFacility.borrower.slug
          }
          fundingRequestId={fundingRequestId}
        >
          <BorrowerManageViewContainer.FundingRequestManageViewContainer
            desiredStepDefault={desiredStep}
          />
        </BorrowerProviders.FundingRequestsProvider>
      </BorrowerViewTemplate.FundingRequestsViewTemplate>
    );
  }
  return (
    <BorrowerDetailViewContainer.FundingRequestDetailViewContainer
      fundingRequestId={fundingRequestId}
      creditFacilityId={
        fundingRequestQuery.data.fundingRequestWithFullDetails.creditFacilityId
      }
    />
  );
};

const BorrowerRouteBase = () => {
  const flags = useFeatureFlags();
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);

  const fundingRequestQuery = useGetFundingRequestQuery({
    variables: { fundingRequestId },
  });
  if (fundingRequestQuery.error) throw fundingRequestQuery.error;
  if (!fundingRequestQuery.data || fundingRequestQuery.loading) {
    return (
      <>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            {
              type: 'loading',
            },
          ]}
        />
        <HStack justify="space-between" w="100%">
          <Heading as="h2" mt="24px" mb="40px">
            Funding Request
          </Heading>
          <Flex gap="1rem">
            <Skeleton w="150px" h="40px" />
            <Skeleton w="150px" h="40px" />
          </Flex>
        </HStack>
        <Skeleton height="24px" w="300px" mb="24px" />
        <Skeleton height="600px" />
      </>
    );
  }

  if (
    fundingRequestQuery.data.fundingRequestWithFullDetails.status ===
      'pending' &&
    fundingRequestQuery.data.fundingRequestWithFullDetails.type ===
      'origination'
  ) {
    return (
      <Navigate
        to={`/funding-requests/create?creditFacilityId=${fundingRequestQuery.data.fundingRequestWithFullDetails.creditFacilityId}`}
        state={{
          from: `/funding-requests/${fundingRequestId}`,
        }}
      />
    );
  }

  if (
    fundingRequestQuery.data.fundingRequestWithFullDetails.status ===
      'pending' &&
    fundingRequestQuery.data.fundingRequestWithFullDetails.type === 'upsizing'
  ) {
    return (
      <Navigate
        to={`/funding-requests/upsize?creditFacilityId=${fundingRequestQuery.data.fundingRequestWithFullDetails.creditFacilityId}&loanId=${fundingRequestQuery.data.fundingRequestWithFullDetails.loans[0]?.id}`} // TODO: multi-loan @sachinr4
        state={{
          from: `/funding-requests/${fundingRequestId}`,
        }}
      />
    );
  }

  if (flags.displayFundingRequestV2ProviderV2) {
    return (
      <p>
        Hi I am a placeholder for the new provider route for
        /funding-requests/:id routes
      </p>
    );
  }

  return (
    <FundingRequestTemplateProviderV1
      mode={
        fundingRequestQuery.data.fundingRequestWithFullDetails.type ===
        'origination'
          ? 'create'
          : 'upsize'
      }
      allowedActions="readOnly"
      creditFacilityId={
        fundingRequestQuery.data.fundingRequestWithFullDetails.creditFacilityId
      }
      fundingRequestId={fundingRequestId}
      loanId={
        fundingRequestQuery.data.fundingRequestWithFullDetails.loans[0]?.id ?? // TODO: multi-loan @sachinr4
        ''
      }
    >
      <Flex direction="column" gap="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Requests', path: '/funding-requests' },
            {
              label:
                fundingRequestQuery.data.fundingRequestWithFullDetails
                  .creditFacility.name || 'Funding Request',
            },
          ]}
        />
        <HStack justify="space-between" w="100%">
          <Typography variant="page-title">Funding Request</Typography>
          <Flex gap="1rem">
            <FundingRequestStatusBadge
              status={
                fundingRequestQuery.data.fundingRequestWithFullDetails.status
              }
            />
            <ActionsButton
              reports={
                fundingRequestQuery.data.fundingRequestWithFullDetails
                  .fundingRequestReports ?? []
              }
            />
            <FundingRequestEventLogSingleEntityDrawer
              entityId={fundingRequestId}
            />
          </Flex>
        </HStack>
        <Badge
          colorScheme="gray"
          w="fit-content"
          p="4px 8px"
          fontWeight="500"
        >{`ID: ${fundingRequestId}`}</Badge>

        {flags.displayFundingRequestV2Alpha ? (
          <SubmitFundingRequestForAlphaCustomers />
        ) : (
          // @jendevelops Update to get the type from context, since create route template provider is sometimes responsible for initing the FR if it
          <SubmitFundingRequest type="origination" />
        )}
        {/* This should only show up for borrowers, so when org type removed, will need to update this to also hide on non borrower */}
        <CancelFundingRequestButton
          fundingRequestId={fundingRequestId}
          creditFacilityId={
            fundingRequestQuery.data.fundingRequestWithFullDetails
              .creditFacilityId
          }
          fundingRequestStatus={
            fundingRequestQuery.data.fundingRequestWithFullDetails.status
          }
        />
      </Flex>
    </FundingRequestTemplateProviderV1>
  );
};

// need new lender can send funding request route base

// merges in old borrower routes w/ approval for submitted FRs

const LenderCanSubmitFundingRequestRouteBase = () => {
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);
  const flags = useFeatureFlags();

  const { data, loading, error } = useGetFundingRequestQuery({
    fetchPolicy: 'no-cache',
    variables: { fundingRequestId },
  });

  if (loading) {
    return <Skeleton height="600px" />;
  }

  // data should only be undefined/null on page load
  // no FR means frID is incorrect, or some other weird error is going on
  if (!data) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Funding Request not found</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon /> <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  // if a FR is pending, it is a WIP FR
  // that means we need to display the old borrower funding request flow
  if (data.fundingRequestWithFullDetails.status === 'pending') {
    return flags.displayFundingRequestV2 ||
      flags.displayFundingRequestV2ProviderV2 ? (
      <BorrowerRouteBase />
    ) : (
      <BorrowerRouteBaseOld />
    );
  }
  // otherwise, we can just display the lender details page (that has approve/reject buttons for FR.status = submitted)
  return <LenderRouteBase />;
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="/funding-requests" />;
    }}
  >
    <Flex direction="column" padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <PermissionGuard
        permissions={(context) => {
          if (context.user.userType !== 'CAPITAL_PROVIDER') return 'deny';
          return createFundingRequestPermissions(context);
        }}
      >
        <LenderCanSubmitFundingRequestRouteBase />
      </PermissionGuard>
      <PermissionGuard
        permissions={(context) => {
          if (context.user.userType !== 'CAPITAL_PROVIDER') return 'deny';
          // if you can create funding request, should be directed to LenderCanSubmitFundingRequestRouteBase
          if (createFundingRequestPermissions(context) === 'allow')
            return 'deny';
          return 'allow';
        }}
      >
        <LenderRouteBase />
      </PermissionGuard>
      <PermissionGuard
        permissions={(context) => {
          if (context.user.userType !== 'BORROWER') return 'deny';
          if (
            !context.flags.displayFundingRequestV2 &&
            !context.flags.displayFundingRequestV2ProviderV2
          )
            return 'deny';
          return 'allow';
        }}
      >
        <BorrowerRouteBase />
      </PermissionGuard>
      <PermissionGuard
        permissions={(context) => {
          if (context.user.userType !== 'BORROWER') return 'deny';
          if (context.flags.displayFundingRequestV2) return 'deny';
          return 'allow';
        }}
      >
        <BorrowerRouteBaseOld />
      </PermissionGuard>
    </Flex>
  </RouteErrorBoundary>
);
